import {MenuBar, SkipToSection} from '@mvps-genesis/accessibility';
import {PageManager} from '@mvps-genesis/bootstrap';
import 'custom-extensions/g-modal';
import 'custom-extensions/ui-components/x-guide-card';
import 'custom-extensions/ui-components/x-product-card';
import 'custom-extensions/ui-components/x-video-card';
import 'custom-extensions/ui-components/x-category-card';
import 'custom-extensions/ui-components/x-discussion-card';
import 'custom-extensions/ui-components/x-media-card';
import {initCarousels} from 'extensions/carousel';
import ConfirmAction from 'extensions/confirm-action';
import ContinueShopping from 'extensions/continue-shopping';
import dynamicFormElementAttributes from 'extensions/dynamic-form-element-attributes';
import {GlobalMessageContainer} from 'extensions/messages';
import MiniBasket from 'extensions/mini-basket';
import NavigationMenu from 'extensions/navigation-menu';
import './Global.scss';

export default class Global extends PageManager {

	// eslint-disable-next-line max-lines-per-function
	constructor (pageContext) {

		super(pageContext);

		this.initSkipToSection();

		/**
		 * Create global message container
		 */
		new GlobalMessageContainer({container: '#js-messages'});

		/**
		 * Initialize carousels
		 */
		initCarousels();

		this.initializeHTMLFormValidator();
		this.initializeSubmitEvents();

		/**
		 * Initialize the Dynamic Form-Element Attributes
		 */
		dynamicFormElementAttributes();

		this.initModals();
		this.initLazyload();
		this.initCookieConsent();
		this.initAddressEvents();
		this.initScrollTo();
		this.initBackToTop();
		this.initStickyListener();
		this.initAjaxLoginRegister();
		this.initFormLoadingUI();
		this.initTooltip();
		this.initTabbedCarousels();
		this.initGTM();

	}

	// eslint-disable-next-line max-lines-per-function
	onReady () {

		/**
		 * Initialize the Navigation Menu extension
		 */
		const mainMenuElem = document.querySelector('.js-navigation-menu');
		if (mainMenuElem) {
			new NavigationMenu({
				allParentMenus: '.js-navigation-menu__parent',
				drawers: '.js-navigation-menu__drawer',
				mainMenu: mainMenuElem,
				mainMenuCloseButtons: '.js-navigation-menu__close',
				mainMenuOpenButtons: '.js-navigation-menu__open',
				menuItems: '.js-navigation-menu__link',
				mobileFooter: '.js-navigation-menu__contact',
				mobileHeader: '.js-navigation-menu__account',
				parentMenus: '.js-navigation-menu__has-child',
				showPreviousMenuButtons: '.js-navigation-menu__previous'
			});
		}

		const accountLinks = document.getElementById('js-customer-account-links');
		if (accountLinks) {
			// Basic instance constructor
			new MenuBar({
				breakpoint: false,
				element: accountLinks,
				navigateOnDown: true,
				navigateOnEnter: true,
				navigateOnSpace: true
			});
		}

		const staticLinks = document.getElementById('js-static-page-links');
		if (staticLinks) {
			// Basic instance constructor
			new MenuBar({
				element: staticLinks,
				navigateOnDown: true,
				navigateOnEnter: true,
				navigateOnSpace: true
			});
		}

		/**
		 * Initialize the Mini-Basket extension
		 */
		if (document.querySelector('.js-mini-basket')) {
			new MiniBasket({
				openOnAddToCart: true
			});
		}

		/**
		 * Initialize continue shopping link
		 */
		new ContinueShopping();
		this.searchFormEvents();
		this.initSearchAutocomplete();
		this.triggerPrint();
		this.initCollapsibleMenu();

		new ConfirmAction();

	}

	initSkipToSection () {
		const sections = [];

		const searchElem = document.querySelector('[itemprop="query-input"]');

		if (searchElem) {
			sections.push({
				label: 'Search',
				targetElement: searchElem
			});
		}

		const facetsElem = document.getElementById('js-facets');

		if (facetsElem) {
			sections.push({
				label: 'Filters',
				targetElement: facetsElem
			});
		}

		SkipToSection.createFromArray(sections);
	}

	async initLazyload () {
		// Elements
		const lazyLoadElements = document.getElementsByClassName('lazyload');

		if (lazyLoadElements.length === 0) {
			return;
		}

		// Module
		await import('lazysizes/plugins/unveilhooks/ls.unveilhooks');
		await import('lazysizes/plugins/bgset/ls.bgset');
		await import('lazysizes/plugins/custommedia/ls.custommedia');
		await import('lazysizes');
	}

	async initCookieConsent () {
		const cookieName = `mm5-${this.pageContext.Store_Code}-genesis-cookie-consent`;

		if (document.cookie.indexOf(cookieName) !== -1) {
			return;
		}

		const {CookieConsent} = await import('extensions/cookie-consent');

		let contentLink = 'https://www.cookiesandyou.com/';
		let contentLinkTarget = '_blank';

		if (this.pageContext.Privacy_Policy_Url) {
			contentLink = this.pageContext.Privacy_Policy_Url;
			contentLinkTarget = '';
		}

		new CookieConsent({
			content: {
				dismiss: 'Got it!',
				href: contentLink,
				link: 'Learn More',
				message: 'This website uses cookies to ensure you get the best experience on our website.',
				target: contentLinkTarget
			},
			cookie: {
				name: cookieName,
				secure: true
			},
			palette: {
				button: {
					background: '#F9F9F9',
					text: '#000000'
				},
				popup: {
					background: '#222222',
					text: '#FFFFFF'
				}
			}
		});
	}

	async initializeHTMLFormValidator () {
		const forms = document.getElementsByClassName('js-form-validator');

		if (forms.length === 0) {
			return;
		}

		const {HTMLFormValidator} = await import('extensions/form-validation');

		for (const form of forms) {
			form.HTMLFormValidator = new HTMLFormValidator({
				fieldContainer: '.g-form-list__item',
				formElement: form
			});
		}
	}

	async initializeSubmitEvents () {
		const forms = document.getElementsByClassName('js-submit-loader');

		if (forms.length === 0) {
			return;
		}

		const {FormSubmitEvents} = await import('extensions/form-submit-events');

		for (const form of forms) {
			form.submitLoaderEvent = new FormSubmitEvents({
				formElement: form
			});
		}
	}

	async initModals () {
		const hasModalTrigger = document.querySelector(`[data-micromodal-trigger]`);

		if (!hasModalTrigger) {
			return;
		}

		const {default: Modal} = await import('extensions/modal');
		const modal = new Modal();

		/**
		 * Initialize modals:
		 * 	Pass list of modal ids without an overlay that should close when
		 * 	clicking off of modal.
		 */
		modal.init([
			'js-global-account',
			'js-global-customer-service',
			'js-parts-identifier-steps'
		]);
	}

	async initAddressEvents () {
		const datalists = document.querySelectorAll('[data-address-datalist]');
		const toggles = document.querySelectorAll('[data-address-toggle]');

		if (datalists.length === 0 && toggles.length === 0) {
			return;
		}

		const {AddressEvents} = await import('extensions/address-events');

		new AddressEvents(datalists, toggles);
	}

	searchFormEvents () {
		const searchForm = document.getElementById('js-search-form');
		const searchInput = document.getElementById('js-search-form-input');
		const searchClearButton = document.getElementById('js-site-header__search-clear');

		if (searchForm && searchInput && searchClearButton) {
			this.searchFormEvent(searchInput, searchClearButton);
		}

		const searchFormMobile = document.getElementById('js-search-form-mobile');
		const searchInputMobile = document.getElementById('js-search-form-input-mobile');
		const searchClearButtonMobile = document.getElementById('js-site-header__search-clear-mobile');

		if (searchFormMobile && searchInputMobile && searchClearButtonMobile) {
			this.searchFormEvent(searchInputMobile, searchClearButtonMobile);
		}
	}

	searchFormEvent (searchInput, searchClearButton) {
		if (searchClearButton) {
			const clearButtonToggleClass = 'is-visible';

			searchClearButton.addEventListener('click', () => {
				searchInput.value = '';
				searchClearButton.classList.remove(clearButtonToggleClass);
				searchClearButton.setAttribute('aria-hidden', 'true');
			});

			searchInput.addEventListener('input', () => {
				searchClearButton.classList.toggle(clearButtonToggleClass, searchInput.value);
				if (searchClearButton.classList.contains(clearButtonToggleClass)) {
					searchClearButton.setAttribute('aria-hidden', 'false');
				} else {
					searchClearButton.setAttribute('aria-hidden', 'true');
				}
			});
		}
	}

	/*
	 * The Algolia autocomplete bundle (autocomplete-js, query suggestions, etc.) is fairly
	 * heavy. #js-search-form/#js-search-form-mobile now hold a static .js-search-placeholder
	 * (see cssui-global-header.mvt) styled to look identical to the real autocomplete-js
	 * output, so there's something real to click/focus before the bundle loads. Loading it
	 * on first focus into either container avoids paying for it on every page load.
	 *
	 * The placeholder is left untouched until the import has actually resolved, rather than
	 * being cleared up front — clearing early left the container empty for the whole network
	 * round trip, which read as a layout "jump" once the real component finally popped in.
	 * Leaving it in place means the swap only ever happens once the replacement is already
	 * mounted and ready.
	 *
	 * Both containers' placeholders are removed together once loading finishes (not per
	 * container): the imported module mounts INTO both #js-search-form and
	 * #js-search-form-mobile unconditionally as soon as it loads (see lib/autocomplete.js),
	 * regardless of which one the user actually interacted with, and autocomplete-js appends
	 * its real markup alongside existing content rather than replacing it — so a container
	 * whose placeholder was never removed would end up with both stacked on top of each other
	 * the next time it becomes relevant (e.g. resizing to the breakpoint that reveals it).
	 */
	// eslint-disable-next-line max-lines-per-function
	initSearchAutocomplete () {
		const containers = [
			document.getElementById('js-search-form'),
			document.getElementById('js-search-form-mobile')
		].filter(Boolean);

		if (containers.length === 0) {
			return;
		}

		// Guards against a real page navigation if Enter/submit is pressed on the placeholder before the real component has replaced it.
		containers.forEach(container => {
			container.querySelector('.js-search-placeholder form')?.addEventListener('submit', (event) => {
				event.preventDefault();
			});
		});

		let autocompleteLoadPromise = null;
		const loadAutocomplete = () => {
			if (!autocompleteLoadPromise) {
				autocompleteLoadPromise = import('custom-extensions/algolia/autocomplete').then(() => {
					containers.forEach(container => {
						container.querySelector('.js-search-placeholder')?.remove();
					});
				});
			}
			return autocompleteLoadPromise;
		};

		const containerLoadAutocomplete = async (container) => {
			await loadAutocomplete();

			/*
			 * Below autocomplete-js's --aa-detached-media-query breakpoint (max-width: 680px,
			 * which covers #js-search-form-mobile), it mounts an aa-DetachedSearchButton that
			 * opens a full-screen modal on click, rather than a focusable inline input — so
			 * focusing does nothing there and it needs an actual click to open.
			 */
			const detachedButton = container.querySelector('.aa-DetachedSearchButton');
			if (detachedButton) {
				detachedButton.click();
			} else {
				container.querySelector('input')?.focus();
			}
		};

		for (const container of containers) {
			if (document.activeElement === container) {
				containerLoadAutocomplete(container);
				continue;
			}

			/*
			 * Safari (desktop and iOS) doesn't move focus to a <button> on click/tap the way
			 * Chrome/Firefox do, so 'focusin' alone never fires for the mobile container's
			 * aa-DetachedSearchButton there. Listening for 'click' too covers that tap.
			 */
			const controller = new AbortController();
			const trigger = () => {
				controller.abort();
				containerLoadAutocomplete(container);
			};
			container.addEventListener('focusin', trigger, {signal: controller.signal});
			container.addEventListener('click', trigger, {signal: controller.signal});
		}
	}

	triggerPrint () {

		/**
		 *  Launch Printer Dialog
		 */
		const printTriggers = document.getElementsByClassName('js-trigger-print');

		if (printTriggers.length) {
			for (const element of printTriggers) {
				element.addEventListener('click', function (e) {
					e.preventDefault();
					window.print();
				}, false);
			}
		}

	}

	async initScrollTo () {
		const triggerElements = document.getElementsByClassName('js-scroll-to');

		if (!triggerElements.length) {
			return;
		}

		const {default: ScrollTo} = await import('extensions/scroll-to');

		new ScrollTo(triggerElements, {
			scrollIntoViewOptions: {
				behavior: 'smooth',
				block: 'end',
				inline: 'nearest'
			}
		});
	}

	async initBackToTop () {

		const backToTopButton = document.getElementById('js-back-to-top-button');

		if (!backToTopButton) {
			return;
		}

		const backToTopClass = 'g-back-to-top-button--shown';

		const {default: ScrollTo} = await import('extensions/scroll-to');

		new ScrollTo('#js-back-to-top-button', {
			scrollIntoViewOptions: {
				behavior: 'smooth',
				inline: 'nearest'
			}
		});

		let lastScrollTop = 0;

		window.addEventListener('scroll', () => {
			this.showHideBackToTop(backToTopButton, backToTopClass);

			// Detect if user is scrolling up or down
			let st = window.pageYOffset || document.documentElement.scrollTop;
			if (st > lastScrollTop) {
				document.body.classList.remove('g-user-scrolling-up');
			} else {
				document.body.classList.add('g-user-scrolling-up');
			}

			lastScrollTop = st <= 0 ? 0 : st;
		});

		this.showHideBackToTop(backToTopButton, backToTopClass);

	}

	showHideBackToTop (backToTopButton, backToTopClass) {

		const heightToShowButton = window.innerHeight;
		const offset = heightToShowButton / 2;
		const windowOffset = offset + (isNaN(window.pageYOffset) ? 0 : window.pageYOffset);

		if (windowOffset >= heightToShowButton) {
			backToTopButton.classList.add(backToTopClass);
		} else {
			backToTopButton.classList.remove(backToTopClass);
		}

	}

	initStickyListener () {
		if (!('IntersectionObserver' in window)) {
			return;
		}

		const header = document.querySelector('.g-site-header:not(.g-site-header__unsticky)');

		if (!header) {
			return;
		}

		const observer = new IntersectionObserver(
			([e]) => e.target.classList.toggle('g-site-header__sticky', e.intersectionRatio < 1),
			{threshold: [1]}
		);

		observer.observe(header);
	}

	async initAjaxLoginRegister () {
		const loginRegisterElement = document.getElementById('js-login-register');

		if (!loginRegisterElement) {
			return;
		}

		const {AjaxLoginRegister} = await import('extensions/ajax-login-register');

		new AjaxLoginRegister(loginRegisterElement);
	}

	async initFormLoadingUI () {
		const formElements = document.getElementsByClassName('js-form-show-loading');

		if (!formElements.length) {
			return;
		}

		const {default: LoadingUI} = await import('extensions/loading-ui');
		const loadingContainerSelector = '.js-loading-container';
		const loadingElementSelector = '.js-loading-element';

		for (const formElement of formElements) {
			const loadingContainerElement = formElement.closest(loadingContainerSelector);

			if (!loadingContainerElement) {
				continue;
			}

			const loadingElement = loadingContainerElement.querySelector(loadingElementSelector);

			if (!loadingElement) {
				continue;
			}

			formElement.LoadingUI = new LoadingUI(loadingElement, {
				overlayElement: loadingContainerElement.hasAttribute('data-disable-loading-overlay') ? false : loadingContainerElement
			});

			formElement.addEventListener('submit', () => {
				formElement.LoadingUI.show();
			});
		}
	}

	async initTooltip () {

		const tooltipElements = document.getElementsByClassName('js-tooltip');

		if (!tooltipElements.length) {
			return;
		}

		const {default: Tooltip} = await import('custom-extensions/tooltip');

		for (const element of tooltipElements) {
			new Tooltip(element);
		}
	}

	async initCollapsibleMenu () {

		const menuElements = document.getElementsByClassName('js-collapsible-menu');

		if (menuElements.length === 0) {
			return;
		}

		const {default: CollapsibleMenu} = await import('extensions/collapsible-menu');

		new CollapsibleMenu(menuElements);

	}

	async initTabbedCarousels () {

		const tabbedCarouselElements = document.getElementsByClassName('js-tabbed-carousel');

		if (!tabbedCarouselElements.length) {
			return;
		}

		const {default: TabbedCarousels} = await import('extensions/tabbed-carousels');

		new TabbedCarousels(tabbedCarouselElements);

	}

	async initGTM () {

		const {default: GTM} = await import('extensions/gtm');

		const gtm = new GTM(this.pageContext);
		gtm.init();

		// Listen for Print Events
		window.addEventListener('beforeprint', () => {
			gtm.sendEvent({
				action: 'Click',
				category: 'Print',
				label: document.title,
				noninteractionHit: false
			});
		});
	}

}
